/**
 * App wrapper — 백버튼 (Android), 일시정지/재개, deep link (appUrlOpen).
 *
 * Notion §2.3.5 (선물 deep link 수령), §7.2 (OAuth deep link).
 * Phase 4 (인증) 에서 OAuth deep link 핸들러 등록.
 */
import { useEffect } from "react";
import { isNative, nativeWarn } from "./index";

export type DeepLinkHandler = (url: URL) => void | Promise<void>;
export type BackButtonHandler = (canGoBack: boolean) => boolean;
//                                                       ↑ true 반환 시 default 동작 (history.back / exit) 막음

/**
 * Deep link 수신 — peeka://oauth/apple/callback?code=... 같은 URL.
 *
 * 사용 예 (App.tsx):
 *   useDeepLink((url) => {
 *     if (url.host === 'oauth') handleOauthCallback(url);
 *     else if (url.pathname.startsWith('/gift/')) handleGift(url);
 *   });
 */
// 앱 생명주기 동안 launch URL 은 1회만 소비 (warm start 에서 getLaunchUrl 이 stale 한
// cold-launch URL 을 반환해 중복 navigate 되는 것을 방지).
//
// 모듈 변수만으로는 부족하다: 로그아웃의 window.location.replace('/') 하드 리로드가 JS 를
// 처음부터 재실행해 이 변수를 false 로 되돌리는데, 네이티브 프로세스는 살아있어
// getLaunchUrl 은 여전히 stale launch URL(예: 애플 가입 중 복귀한
// peeka://oauth/apple/callback)을 반환한다. 그러면 리로드 후 그 딥링크가 재발화해
// 이미 소비된 code 로 콜백을 다시 태워 "가입 실패" 가 난다.
// 해결: 소비 플래그를 sessionStorage 에도 박는다 — 같은 웹뷰의 하드 리로드는 넘어 생존하고,
// 진짜 콜드스타트(새 웹뷰)면 비워져 정상적으로 1회만 소비된다.
const LAUNCH_URL_CONSUMED_KEY = "template-launch-url-consumed";
let launchUrlConsumed = false;

function isLaunchUrlConsumed(): boolean {
  if (launchUrlConsumed) return true;
  try {
    return sessionStorage.getItem(LAUNCH_URL_CONSUMED_KEY) === "1";
  } catch {
    // 일부 webview 는 storage 접근 시 throw — 메모리 플래그로만 판단.
    return false;
  }
}

function markLaunchUrlConsumed(): void {
  launchUrlConsumed = true;
  try {
    sessionStorage.setItem(LAUNCH_URL_CONSUMED_KEY, "1");
  } catch {
    // best-effort — 저장 실패해도 메모리 플래그로 이번 생명주기는 가드된다.
  }
}

export function useDeepLink(handler: DeepLinkHandler): void {
  useEffect(() => {
    if (!isNative()) return;
    let cleanup: (() => void) | null = null;

    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("appUrlOpen", async (event) => {
          try {
            const url = new URL(event.url);
            await handler(url);
          } catch (err) {
            nativeWarn("app:deepLink:parse", err);
          }
        });
        cleanup = () => handle.remove();

        // Cold start — 앱이 꺼진 상태에서 App Link/Universal Link 로 켜진 경우,
        // listener 등록 시점이 native 의 launch 이벤트보다 늦어 appUrlOpen 이 누락될 수 있다.
        // launch URL 을 1회 직접 확인해 선물 페이지 등으로 라우팅한다.
        if (!isLaunchUrlConsumed()) {
          markLaunchUrlConsumed();
          const launch = await App.getLaunchUrl();
          if (launch?.url) {
            try {
              await handler(new URL(launch.url));
            } catch (err) {
              nativeWarn("app:deepLink:launch", err);
            }
          }
        }
      } catch (err) {
        nativeWarn("app:deepLink:register", err);
      }
    })();

    return () => {
      cleanup?.();
    };
  }, [handler]);
}

/**
 * Android 하드웨어 백버튼 핸들러.
 *
 * @param handler  반환 true = default (history.back or exit) 막고 직접 처리
 *                 반환 false = default 동작 진행
 */
export function useBackButton(handler: BackButtonHandler): void {
  useEffect(() => {
    if (!isNative()) return;
    let cleanup: (() => void) | null = null;

    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("backButton", ({ canGoBack }) => {
          const handled = handler(canGoBack);
          if (!handled) {
            if (canGoBack) {
              window.history.back();
            } else {
              void App.exitApp();
            }
          }
        });
        cleanup = () => handle.remove();
      } catch (err) {
        nativeWarn("app:backButton", err);
      }
    })();

    return () => {
      cleanup?.();
    };
  }, [handler]);
}

/** 앱 일시정지 / 재개 이벤트 (예: 보안 화면 띄우기) */
export function useAppState(onChange: (active: boolean) => void): void {
  useEffect(() => {
    if (!isNative()) return;
    let cleanup: (() => void) | null = null;

    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("appStateChange", (state) => {
          onChange(state.isActive);
        });
        cleanup = () => handle.remove();
      } catch (err) {
        nativeWarn("app:state", err);
      }
    })();

    return () => {
      cleanup?.();
    };
  }, [onChange]);
}

export async function exitApp(): Promise<void> {
  if (!isNative()) return;
  try {
    const { App } = await import("@capacitor/app");
    await App.exitApp();
  } catch (err) {
    nativeWarn("app:exit", err);
  }
}

/**
 * 네이티브 앱 정보 — versionName / versionCode(build). 웹이면 null.
 * build = Android versionCode (= gradle GITHUB_RUN_NUMBER / CI 빌드 순번),
 * iOS 는 CFBundleVersion. 어느 빌드(vc)가 설치됐는지 디버그 메뉴에서 확인용.
 */
export async function getNativeAppInfo(): Promise<{ version: string; build: string } | null> {
  if (!isNative()) return null;
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    return { version: info.version, build: info.build };
  } catch (err) {
    nativeWarn("app:getInfo", err);
    return null;
  }
}
